import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api-client';
import { getBundle, getTile, getFazMaxZoom, saveBundle } from '../visita/offline';
import { visitaBundleSchema } from '../visita/schemas';
import {
  GOOGLE_SAT_TILE,
  GOOGLE_LABELS_TEMPLATE,
  OFFLINE_MIN_ZOOM,
  centerOf,
  registerSentinelMosaic,
  s2NdviTemplate,
  s2NdmiTemplate,
  s2SaviTemplate,
  s2GndviTemplate,
  s2NdreTemplate,
  s2NdwiTemplate,
  tileFromTemplate,
  tilesForGeojsons,
} from '../visita/staticmap';

// Zoom estimado que enquadra um bounds num container ~380×430, SEM depender do
// tamanho real do container (que no device às vezes é 0 no init) — assim o mapa
// abre já sobre a fazenda/talhão (tiles em cache), nunca "em branco" no zoom-out.
function estFitZoom(b: L.LatLngBounds, maxZoom: number): number {
  const dLon = Math.max(Math.abs(b.getEast() - b.getWest()), 1e-5);
  const dLat = Math.max(Math.abs(b.getNorth() - b.getSouth()), 1e-5);
  const zLon = Math.log2((360 / dLon) * (380 / 256));
  const zLat = Math.log2((170 / dLat) * (430 / 256));
  // Clamp em 12 (OFFLINE_MIN_ZOOM): o ENQUADRAMENTO automático nunca abre mais
  // afastado que antes — o minZoom 8 é só pro zoom-out MANUAL do usuário.
  return Math.max(OFFLINE_MIN_ZOOM, Math.min(maxZoom, Math.floor(Math.min(zLon, zLat))));
}

// 1×1 transparente — tile ausente no offline (evita img quebrada).
const TRANSPARENT_TILE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

// Zoom mínimo do MAPA (≠ OFFLINE_MIN_ZOOM=12, que é o alcance do cache de tiles):
// fazendas grandes precisam afastar mais pra caber inteiras na tela.
const MAP_MIN_ZOOM = 8;

// Desktop (≥1024px): o mapa é coluna dedicada — interação direta sem tela cheia.
const isDesktop = () =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;

const EXPAND = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#1a1a19" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"/></svg>';
const CLOSE = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#1a1a19" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const LAYERS = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#1a1a19" stroke-width="1.8" stroke-linejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>';
const MAP_SVG = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#1a1a19" stroke-width="2" stroke-linejoin="round"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z"/><path d="M9 4v14M15 6v14"/></svg>';
const CTL_CSS =
  'width:38px;height:38px;border-radius:11px;background:#fff;border:none;box-shadow:0 2px 8px rgba(0,0,0,.25);' +
  'cursor:pointer;font-size:20px;color:#1a1a19;display:flex;align-items:center;justify-content:center;margin-top:8px';

// Pin oficial ARA (map_leaf): gota coral com broto — SVG do design (Ativo 1map_leaf).
const PIN_SVG =
  '<svg width="26" height="32" viewBox="0 0 52.49 64.98" xmlns="http://www.w3.org/2000/svg">' +
  '<path fill="#e96a45" d="M52.49,26.83C52.49,12.2,40.79.32,26.25,0,11.7.32,0,12.2,0,26.83c0,5.4,1.6,10.43,4.34,14.63h0s2.42,5.44,21.9,23.52c19.48-18.07,21.9-23.51,21.9-23.51h0c2.75-4.21,4.34-9.23,4.34-14.64Z"/>' +
  '<circle fill="#fff" opacity=".19" cx="26.84" cy="26.83" r="19.68"/>' +
  '<path fill="#e3e3e3" d="M25.91,35.83v-7.98c-1.07,0-2.09-.2-3.08-.61s-1.85-.99-2.6-1.74-1.33-1.62-1.73-2.6-.6-2.01-.6-3.08v-2h2c1.05,0,2.07.2,3.05.61s1.85.99,2.6,1.74c.52.52.95,1.08,1.29,1.7s.6,1.28.79,1.98c.08-.12.18-.23.28-.34s.21-.22.33-.34c.75-.75,1.62-1.33,2.6-1.74s2.01-.61,3.08-.61h2v2c0,1.07-.2,2.09-.61,3.08-.41.98-.99,1.85-1.74,2.6s-1.61,1.33-2.59,1.73-2,.6-3.06.6v5h-2ZM25.91,25.83c0-.8-.15-1.56-.46-2.29s-.75-1.37-1.31-1.94-1.21-1-1.94-1.31-1.49-.46-2.29-.46c0,.8.15,1.57.45,2.3s.73,1.38,1.3,1.95c.57.57,1.22,1,1.95,1.3s1.5.45,2.3.45ZM27.91,28.83c.8,0,1.56-.15,2.29-.45s1.37-.73,1.94-1.3,1-1.22,1.31-1.95.46-1.5.46-2.3c-.8,0-1.57.15-2.3.46s-1.38.75-1.95,1.31-1,1.21-1.3,1.94-.45,1.49-.45,2.29Z"/></svg>';

const LAYER_LABELS: Record<string, string> = {
  sat: 'Satélite', ndvi: 'NDVI', ndmi: 'NDMI',
  savi: 'SAVI', gndvi: 'GNDVI', ndre: 'NDRE', ndwi: 'NDWI',
};

if (typeof document !== 'undefined' && !document.getElementById('offmap-css')) {
  const s = document.createElement('style');
  s.id = 'offmap-css';
  s.textContent =
    '.offmap-menu{position:absolute;right:0;top:46px;background:#fff;border-radius:12px;box-shadow:0 6px 20px rgba(0,0,0,.3);' +
    'padding:5px;display:none;min-width:130px}.offmap-menu.on{display:block}' +
    '.offmap-menu button{display:block;width:100%;text-align:left;border:none;background:none;padding:9px 12px;border-radius:9px;' +
    "font:500 14px/1.2 -apple-system,sans-serif;color:#1a1a19;cursor:pointer}.offmap-menu button:hover{background:#f1f1ef}" +
    '.offmap-menu button.on{color:#e96a45;font-weight:600}' +
    // Tela cheia (qualquer device): empurra os controles abaixo da status bar.
    '.offmap-fs .leaflet-top{top:calc(env(safe-area-inset-top,0px) + 44px)}' +
    // Mobile: só o botão de tela cheia da coluna esquerda (o do topo-direito é do desktop).
    '.offmap-fs-top{display:none!important}' +
    // Desktop (≥1024px): controles da ESQUERDA (zoom, centralizar, localização) vão pra
    // coluna DIREITA, abaixo de tela-cheia+camadas. Tela cheia sobe pro topo da coluna.
    '@media (min-width:1024px){' +
    '.offmap .leaflet-top.leaflet-left{left:auto;right:0;top:96px}' +
    '.offmap .leaflet-top.leaflet-left .leaflet-control{margin-left:0;margin-right:10px}' +
    '.offmap .offmap-fs-top{display:flex!important}' +
    '.offmap .offmap-fs-side{display:none!important}' +
    '.offmap-fs .leaflet-top.leaflet-left{top:calc(env(safe-area-inset-top,0px) + 148px)}' +
    '.offmap-fs .leaflet-top.leaflet-right{top:calc(env(safe-area-inset-top,0px) + 44px)}' +
    '}';
  document.head.appendChild(s);
}

// Mapa interativo OFFLINE (Leaflet): satélite + NDVI + NDMI do cache (Dexie), com
// polígonos + pin dos talhões e localização do usuário. Pan/zoom; tela cheia
// por CSS. Camadas pré-baixadas na "Sincronizar propriedades".
export interface FotoPonto {
  lat: number;
  lon: number;
  label: string;   // texto curto no marcador (ex.: "1")
  titulo?: string; // tooltip (ex.: "Ponto 1 · Talhão 2")
}

export function OfflineMap({
  imovelId,
  className,
  focusTalhaoId,
  focusBounds,
  fotoPontos,
}: {
  imovelId: number;
  className?: string;
  focusTalhaoId?: number;
  focusBounds?: [[number, number], [number, number]] | undefined;
  fotoPontos?: FotoPonto[] | undefined;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const toggleFsRef = useRef<() => void>(() => {});
  const interactiveRef = useRef<(on: boolean) => void>(() => {});
  const [fs, setFs] = useState(false);
  toggleFsRef.current = () => setFs((v) => !v);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let map: L.Map | null = null;
    let watchId: number | null = null;
    let ro: ResizeObserver | null = null;
    const objectUrls: string[] = [];
    let cancelled = false;

    void (async () => {
      const [cachedBundle, cachedMax] = await Promise.all([getBundle(imovelId), getFazMaxZoom(imovelId)]);
      if (cancelled || !el) return;
      // Online: bundle FRESCO da API (nome de talhão editado aparece na hora) e
      // atualiza o cache. Offline (ou erro): usa o que foi sincronizado.
      let bundle = cachedBundle;
      if (navigator.onLine) {
        try {
          const fresh = await api(`/api/v2/imoveis/${imovelId}/visita-bundle`, { schema: visitaBundleSchema });
          bundle = fresh;
          void saveBundle(imovelId, fresh);
        } catch { /* mantém cache */ }
        if (cancelled || !el) return;
      }
      // Teto de zoom: OFFLINE é o alcance do cache sincronizado; ONLINE o Google
      // serve até ~z20 nativo — sem isso o mapa travava em 17 esticando tile de
      // z16 (borrado) em talhões pequenos.
      const cacheNative = cachedMax ?? 16;    // maior zoom com tile real no cache (até z20)
      const nativeMax = navigator.onLine ? Math.max(cacheNative, 20) : cacheNative;
      const mapMax = nativeMax + 1;           // só +1 de over-zoom (evita esticar demais → menos borrado)
      // minZoom folgado (8): fazendas grandes precisam afastar além do z12 do
      // cache — online o Google cobre qualquer zoom; offline os polígonos seguem visíveis.
      map = L.map(el, { attributionControl: false, zoomControl: true, maxZoom: mapMax, minZoom: MAP_MIN_ZOOM });
      mapRef.current = map;
      // Vista inicial ANTES de qualquer camada — o Leaflet quebra ("reading 'on'/'min'")
      // se um layer/polígono é adicionado sem o mapa ter view. O enquadramento real vem depois.
      map.setView([-13.29, -60.84], 13);

      // Embutido = estático (a página rola); interação só em tela cheia.
      const setInteractive = (on: boolean) => {
        if (!map) return;
        [map.dragging, map.touchZoom, map.scrollWheelZoom, map.doubleClickZoom, map.boxZoom].forEach((h) => {
          if (h) { if (on) h.enable(); else h.disable(); }
        });
      };
      interactiveRef.current = setInteractive;
      // Mobile: mapa embutido estático (dedo conflita com a rolagem da página);
      // interação só em tela cheia. Desktop: mapa é coluna dedicada → sempre
      // interativo (scroll do mouse, arrastar, zoom direto).
      setInteractive(isDesktop());

      // Pré-carrega TODOS os tiles da fazenda (do Dexie) como blob URLs, uma vez,
      // ANTES de montar as camadas. Assim o getTileUrl é SÍNCRONO — o Leaflet pinta
      // os tiles no primeiro render (o carregamento assíncrono anterior só pintava
      // após um zoom/resize). URLs revogadas no unmount.
      const geos = (bundle?.talhoes ?? []).map((t) => t.geojson).filter(Boolean);
      const { tiles: fazTiles } = tilesForGeojsons(geos);
      // sat/ndvi/ndmi têm cache offline (Dexie); savi/gndvi/ndre/ndwi são online-only
      // (Map vazio → getTileUrl cai direto no template do Planetary Computer).
      const urlByLayer: Record<string, Map<string, string>> = {
        sat: new Map(), ndvi: new Map(), ndmi: new Map(),
        savi: new Map(), gndvi: new Map(), ndre: new Map(), ndwi: new Map(),
      };
      await Promise.all(
        (['sat', 'ndvi', 'ndmi'] as const).flatMap((pref) =>
          fazTiles
            .filter((tl) => pref === 'sat' || tl.z <= 15) // NDVI/NDMI só existem até z15
            .map(async (tl) => {
              const blob = await getTile(`${pref}/${tl.z}/${tl.x}/${tl.y}`);
              if (blob) {
                const u = URL.createObjectURL(blob);
                objectUrls.push(u);
                urlByLayer[pref]!.set(`${tl.z}/${tl.x}/${tl.y}`, u);
              }
            }),
        ),
      );
      if (cancelled || !map) { objectUrls.forEach((u) => URL.revokeObjectURL(u)); return; }

      // Templates Sentinel (NDVI/NDMI) do Planetary Computer — preenchidos quando o
      // mosaico é registrado (async). Enquanto vazios, o fallback online fica inativo.
      let ndviTpl = '';
      let ndmiTpl = '';
      let saviTpl = '';
      let gndviTpl = '';
      let ndreTpl = '';
      let ndwiTpl = '';

      // Camada com getTileUrl SÍNCRONO: cache (blob) → online cai pra fonte da camada
      // (sat = Google; ndvi/ndmi = Sentinel) → offline sem tile fica transparente.
      const mkLayer = (pref: string, maxNative: number) => {
        const C = L.TileLayer.extend({
          getTileUrl(coords: L.Coords) {
            const u = urlByLayer[pref]!.get(`${coords.z}/${coords.x}/${coords.y}`);
            if (u) return u;
            if (navigator.onLine) {
              if (pref === 'sat') return GOOGLE_SAT_TILE(coords.z, coords.x, coords.y);
              if (pref === 'ndvi' && ndviTpl) return tileFromTemplate(ndviTpl, coords.z, coords.x, coords.y);
              if (pref === 'ndmi' && ndmiTpl) return tileFromTemplate(ndmiTpl, coords.z, coords.x, coords.y);
              if (pref === 'savi' && saviTpl) return tileFromTemplate(saviTpl, coords.z, coords.x, coords.y);
              if (pref === 'gndvi' && gndviTpl) return tileFromTemplate(gndviTpl, coords.z, coords.x, coords.y);
              if (pref === 'ndre' && ndreTpl) return tileFromTemplate(ndreTpl, coords.z, coords.x, coords.y);
              if (pref === 'ndwi' && ndwiTpl) return tileFromTemplate(ndwiTpl, coords.z, coords.x, coords.y);
            }
            return TRANSPARENT_TILE;
          },
        }) as new (url: string, opts?: L.TileLayerOptions) => L.TileLayer;
        return new C('', { maxZoom: mapMax, minZoom: MAP_MIN_ZOOM, maxNativeZoom: maxNative, errorTileUrl: TRANSPARENT_TILE });
      };
      const s2Max = Math.min(15, nativeMax);
      const layers: Record<string, L.TileLayer> = {
        sat: mkLayer('sat', nativeMax),
        ndvi: mkLayer('ndvi', s2Max),
        ndmi: mkLayer('ndmi', s2Max),
        savi: mkLayer('savi', s2Max),
        gndvi: mkLayer('gndvi', s2Max),
        ndre: mkLayer('ndre', s2Max),
        ndwi: mkLayer('ndwi', s2Max),
      };
      let active = 'sat';
      layers.sat!.addTo(map);

      // Rótulos de lugar (nomes de município) por cima do satélite, só no
      // zoom-out — que é quando o agrônomo precisa se situar. Some no zoom de
      // trabalho pra não sujar o talhão. Online apenas: não vale cachear texto
      // que ninguém lê offline em cima da fazenda.
      const ZOOM_ROTULOS = 13;
      const rotulos = L.tileLayer(GOOGLE_LABELS_TEMPLATE, {
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        maxZoom: mapMax,
        minZoom: MAP_MIN_ZOOM,
        maxNativeZoom: 21,
        opacity: 0.95,
        errorTileUrl: TRANSPARENT_TILE,
      });
      const sincronizaRotulos = () => {
        if (!map) return;
        const mostrar = navigator.onLine && map.getZoom() <= ZOOM_ROTULOS;
        if (mostrar && !map.hasLayer(rotulos)) rotulos.addTo(map);
        else if (!mostrar && map.hasLayer(rotulos)) map.removeLayer(rotulos);
      };
      map.on('zoomend', sincronizaRotulos);

      // Registra o mosaico Sentinel da cena mais recente sobre a fazenda (online) pra
      // NDVI/NDMI puxarem mesmo sem sync. Idempotente; se falhar, permite nova tentativa
      // no próximo clique. Ao resolver, redesenha a camada ativa pra pintar os tiles.
      let sentinelTried = false;
      const ensureSentinel = () => {
        if (sentinelTried || ndviTpl || !navigator.onLine) return;
        const center = centerOf(geos);
        if (!center) return;
        sentinelTried = true;
        void registerSentinelMosaic(center.lon, center.lat).then((sid) => {
          if (cancelled) return;
          if (!sid) { sentinelTried = false; return; } // deixa tentar de novo
          ndviTpl = s2NdviTemplate(sid);
          ndmiTpl = s2NdmiTemplate(sid);
          saviTpl = s2SaviTemplate(sid);
          gndviTpl = s2GndviTemplate(sid);
          ndreTpl = s2NdreTemplate(sid);
          ndwiTpl = s2NdwiTemplate(sid);
          if (active !== 'sat') layers[active]!.redraw();
        });
      };
      ensureSentinel();

      // Localização do usuário.
      let meDot: L.CircleMarker | null = null;

      // Polígonos + pin (coral) + nome abaixo.
      let bounds: L.LatLngBounds | null = null;
      // Alvo de foco: bounds recebido (do próprio geojson do talhão — robusto, não
      // depende de casar id no bundle); senão cai no match por talhao_id.
      let focusB: L.LatLngBounds | null = focusBounds ? L.latLngBounds(focusBounds) : null;
      for (const t of bundle?.talhoes ?? []) {
        if (!t.geojson) continue;
        try {
        const gj = L.geoJSON(t.geojson as GeoJSON.GeoJsonObject, {
          style: { color: '#ffffff', weight: 1.25, fillColor: '#ffffff', fillOpacity: 0.06 },
        }).addTo(map);
        const b = gj.getBounds();
        if (b.isValid()) {
          bounds = bounds ? bounds.extend(b) : b;
          if (!focusB && focusTalhaoId != null && t.talhao_id === focusTalhaoId) focusB = b;
          const center = b.getCenter();
          const tb = b;
          // Rótulo: nome do talhão (KML/edição) > "Talhão <numero>".
          const nome = String(t.nome || `Talhão ${t.numero}`).replace(/</g, '&lt;');
          // Pin clicável: toque no pin → zoom centralizado no talhão.
          const pin = L.marker(center, {
            interactive: true,
            keyboard: false,
            icon: L.divIcon({
              className: '',
              iconSize: [26, 32],
              iconAnchor: [13, 32],
              html:
                `<div style="position:relative;width:26px;height:32px">` +
                PIN_SVG +
                `<span style="position:absolute;left:50%;top:33px;transform:translateX(-50%);white-space:nowrap;` +
                `color:#fff;font:600 11px/1.2 -apple-system,'SF Pro Text',Inter,sans-serif;` +
                `text-shadow:0 1px 3px rgba(0,0,0,.9),0 0 2px rgba(0,0,0,.8)">${nome}</span></div>`,
            }),
          }).addTo(map);
          pin.on('click', () => { if (map && tb.isValid()) map.fitBounds(tb, { padding: [40, 40] }); });
          // Toque no talhão (fora do pin) → mesmo zoom centralizado do pin.
          gj.on('click', () => { if (map && tb.isValid()) map.fitBounds(tb, { padding: [40, 40] }); });
        }
        } catch { /* talhão com geojson ruim — pula, não quebra o mapa */ }
      }
      // Pontos onde as fotos foram tiradas (revisão da visita): bolinha coral
      // numerada + tooltip. Ponto sem GPS não vira marcador.
      for (const fp of fotoPontos ?? []) {
        try {
          const mk = L.marker([fp.lat, fp.lon], {
            keyboard: false,
            icon: L.divIcon({
              className: '',
              iconSize: [22, 22],
              iconAnchor: [11, 11],
              html:
                `<div style="width:22px;height:22px;border-radius:50%;background:#e96a45;border:2px solid #fff;` +
                `box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;` +
                `color:#fff;font:700 11px/1 -apple-system,'SF Pro Text',Inter,sans-serif">${String(fp.label).replace(/</g, '&lt;')}</div>`,
            }),
          }).addTo(map);
          if (fp.titulo) mk.bindTooltip(fp.titulo, { direction: 'top', offset: [0, -12] });
          bounds = bounds ? bounds.extend([fp.lat, fp.lon]) : L.latLngBounds([[fp.lat, fp.lon], [fp.lat, fp.lon]]);
        } catch { /* coordenada ruim — pula */ }
      }

      // Abre JÁ enquadrado no alvo (talhão em foco, senão a fazenda), com zoom
      // estimado independente do tamanho do container — assim nunca abre "em branco"
      // no zoom-out. O fitBounds no ResizeObserver depois só ajusta fino.
      const initTarget = focusB ?? bounds;
      if (initTarget && initTarget.isValid()) map.setView(initTarget.getCenter(), estFitZoom(initTarget, mapMax));
      else map.setView([-13.29, -60.84], 13);
      sincronizaRotulos();

      if (navigator.geolocation) {
        let meAcc: L.Circle | null = null;
        watchId = navigator.geolocation.watchPosition(
          (pos) => {
            if (cancelled || !map || !mapRef.current) return; // mapa pode ter sido removido
            try {
              const ll: L.LatLngExpression = [pos.coords.latitude, pos.coords.longitude];
              if (!meDot) meDot = L.circleMarker(ll, { radius: 7, color: '#fff', weight: 2.5, fillColor: '#1a73e8', fillOpacity: 1 }).addTo(map);
              else meDot.setLatLng(ll);
              const acc = pos.coords.accuracy || 0;
              if (!meAcc) meAcc = L.circle(ll, { radius: acc, color: '#1a73e8', weight: 1, fillColor: '#1a73e8', fillOpacity: 0.1 }).addTo(map);
              else { meAcc.setLatLng(ll); meAcc.setRadius(acc); }
            } catch { /* mapa removido / estado transitório */ }
          },
          undefined,
          { enableHighAccuracy: true, maximumAge: 5000 },
        );
      }

      // Controles (topo-esquerdo): enquadrar fazenda + localização + tela cheia.
      const mkBtn = (title: string, html: string, onClick: () => void) => {
        const b = L.DomUtil.create('button');
        b.type = 'button';
        b.title = title;
        b.innerHTML = html;
        b.style.cssText = CTL_CSS;
        L.DomEvent.on(b, 'click', (e) => { L.DomEvent.stop(e); onClick(); });
        return b;
      };
      // Enquadramento automático idêntico ao de sempre: nunca abre abaixo do z12
      // (o minZoom 8 do mapa é só pro zoom-out MANUAL).
      const fitAuto = (b: L.LatLngBounds) => {
        if (!map || !b.isValid()) return;
        map.fitBounds(b, { padding: [18, 18] });
        if (map.getZoom() < OFFLINE_MIN_ZOOM) map.setZoom(OFFLINE_MIN_ZOOM);
      };
      const FazBtn = L.Control.extend({
        options: { position: 'topleft' as L.ControlPosition },
        // Na tela do talhão (focusB) o botão enquadra o TALHÃO; na fazenda, a fazenda.
        onAdd: () => mkBtn(focusB ? 'Enquadrar o talhão' : 'Enquadrar a fazenda', MAP_SVG, () => {
          const alvo = focusB ?? bounds;
          if (alvo) fitAuto(alvo);
        }),
      });
      const LocBtn = L.Control.extend({
        options: { position: 'topleft' as L.ControlPosition },
        onAdd: () => mkBtn('Minha localização', '◎', () => {
          if (meDot && map) map.setView(meDot.getLatLng(), Math.min(mapMax, nativeMax + 1));
        }),
      });
      // Tela cheia: no MOBILE fica na coluna esquerda (fs-side); no DESKTOP vai pro
      // topo da coluna direita, ACIMA de camadas (fs-top). CSS mostra um por vez.
      const FsBtn = L.Control.extend({
        options: { position: 'topleft' as L.ControlPosition },
        onAdd: () => { const b = mkBtn('Tela cheia', EXPAND, () => toggleFsRef.current()); b.classList.add('offmap-fs-side'); return b; },
      });
      const FsTopBtn = L.Control.extend({
        options: { position: 'topright' as L.ControlPosition },
        onAdd: () => { const b = mkBtn('Tela cheia', EXPAND, () => toggleFsRef.current()); b.style.marginTop = '10px'; b.classList.add('offmap-fs-top'); return b; },
      });
      map.addControl(new FazBtn());
      map.addControl(new LocBtn());
      map.addControl(new FsBtn());

      // Seletor de camadas (topo-direito): ícone → menu Satélite/NDVI/NDMI.
      const LayerBtn = L.Control.extend({
        options: { position: 'topright' as L.ControlPosition },
        onAdd() {
          const wrap = L.DomUtil.create('div');
          wrap.style.position = 'relative';
          const btn = mkBtn('Camadas', LAYERS, () => menu.classList.toggle('on'));
          btn.style.marginTop = '0';
          wrap.appendChild(btn);
          const menu = L.DomUtil.create('div', 'offmap-menu', wrap);
          (['sat', 'ndvi', 'ndmi', 'savi', 'gndvi', 'ndre', 'ndwi'] as const).forEach((k) => {
            const it = L.DomUtil.create('button', k === active ? 'on' : '', menu);
            it.type = 'button';
            it.textContent = LAYER_LABELS[k]!;
            L.DomEvent.on(it, 'click', (e) => {
              L.DomEvent.stop(e);
              if (map && k !== active) { map.removeLayer(layers[active]!); layers[k]!.addTo(map); active = k; }
              if (k !== 'sat') ensureSentinel(); // qualquer índice Sentinel garante o mosaico online
              Array.prototype.forEach.call(menu.children, (c: Element) => { (c as HTMLElement).className = ((c as HTMLElement).textContent === LAYER_LABELS[k] ? 'on' : ''); });
              menu.classList.remove('on');
            });
          });
          L.DomEvent.disableClickPropagation(wrap);
          return wrap;
        },
      });
      map.addControl(new FsTopBtn()); // primeiro no topo-direito → fica acima de camadas
      map.addControl(new LayerBtn());

      // Enquadra + carrega os tiles SÓ quando o container tem tamanho real.
      // (Antes o fitBounds rodava com tamanho 0/errado → satélite não pintava e o
      //  talhão não era enquadrado até um resize/zoom manual.)
      let fitted = false;
      const doFit = () => {
        if (cancelled || !map || !el) return;
        try {
          map.invalidateSize();
          if (!fitted && el.clientWidth > 0 && el.clientHeight > 0) {
            const fit = focusB ?? bounds;
            if (fit) fitAuto(fit);
            fitted = true;
          }
        } catch { /* mapa já removido / estado transitório */ }
      };
      ro = new ResizeObserver(doFit);
      ro.observe(el);
      requestAnimationFrame(doFit);
      setTimeout(doFit, 120);
      setTimeout(doFit, 400);
    })().catch((e) => {
      // Nunca deixa um erro de montagem do mapa (ex.: estado transitório do Leaflet)
      // quebrar a tela da fazenda — só loga; o pior caso é o mapa não renderizar.
      if (!cancelled) console.error('[OfflineMap] falha ao montar o mapa', e);
    });

    return () => {
      cancelled = true;
      if (watchId != null && navigator.geolocation) navigator.geolocation.clearWatch(watchId);
      ro?.disconnect();
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
      map?.remove();
      mapRef.current = null;
    };
  }, [imovelId, focusTalhaoId, focusBounds, fotoPontos]);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      // Posição dos controles (safe-area no fs, coluna direita no desktop) vive no
      // CSS via .offmap-fs / @media — evita corrida com o mount assíncrono do mapa.
      el.querySelectorAll<HTMLButtonElement>('button[title="Tela cheia"]').forEach((b) => { b.innerHTML = fs ? CLOSE : EXPAND; });
    }
    interactiveRef.current(fs || isDesktop()); // mobile: só em tela cheia; desktop: sempre
    const t = setTimeout(() => mapRef.current?.invalidateSize(), 140);
    return () => clearTimeout(t);
  }, [fs]);

  return (
    <div className={cn('offmap relative bg-surface3', fs && 'offmap-fs fixed inset-0 z-[70] rounded-none', !fs && className)}>
      <div ref={ref} className="h-full w-full" />
    </div>
  );
}
